import { useEffect, useState } from 'react';
import { useActs } from '@/hooks/useActs';
import { useFavorites } from '@/hooks/useFavorites';
import { useVotes } from '@/hooks/useVotes';
import { actEndMs, actLocation, actStartMs, actTitle, isNow, isPast } from '@/lib/actHelpers';
import type { Activity, VotesMap } from '@/api/types';
import styles from './NextUpHero.module.css';

interface NextUpHeroProps {
  slug: string;
}

type Source = 'fav' | 'crew' | 'any';

interface Picked {
  act: Activity;
  source: Source;
}

const TICK_MS = 1_000;

/**
 * Hero-Card oben auf dem Plan-Hub: zeigt den nächsten relevanten Act mit
 * Live-Countdown. Auswahl-Präferenz:
 *   1. Favorisierter Act, der gerade läuft oder als nächstes startet
 *   2. Act mit ≥1 Vote ("Crew-Plan"), der nächste
 *   3. Irgendein nächster Act
 * Fällt komplett aus, wenn nichts in der Zukunft liegt (z.B. nach Trip-Ende).
 */
export function NextUpHero({ slug }: NextUpHeroProps) {
  const actsQuery = useActs(slug);
  const { favoritesSet } = useFavorites(slug);
  const { votes } = useVotes(slug);

  const [now, setNow] = useState(() => Date.now());

  // 1s-Ticker für den Countdown. useEffect räumt das Interval bei Unmount auf.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  if (actsQuery.isLoading || !actsQuery.data) return null;

  const picked = pickNext(actsQuery.data, favoritesSet, votes, now);
  if (!picked) return null;

  const { act, source } = picked;
  const start = actStartMs(act);
  const end = actEndMs(act);
  const running = isNow(act, now);
  const label = sourceLabel(source, running);
  const meta = [actLocation(act)].filter(Boolean).join(' · ');

  return (
    <div className={`${styles.hero} ${running ? styles.running : ''}`} aria-live="polite">
      <div className={styles.inner}>
        <div className={styles.label}>{label}</div>
        <div className={styles.title}>{actTitle(act)}</div>
        {meta ? <div className={styles.meta}>{meta}</div> : null}
        <div className={styles.countdown}>
          {running
            ? `Endet ${formatCountdown(end - now)}`
            : `Startet ${formatCountdown(start - now)}`}
        </div>
        {running && Number.isFinite(start) && Number.isFinite(end) ? (
          <div className={styles.progressTrack} aria-hidden="true">
            <div
              className={styles.progressFill}
              style={{ width: `${progressPercent(start, end, now)}%` }}
            />
          </div>
        ) : null}
        <div className={styles.source}>
          {source === 'fav' ? '★ aus deinen Favoriten'
            : source === 'crew' ? '👥 aus dem Crew-Plan'
            : 'Trip-Programm'}
        </div>
      </div>
    </div>
  );
}

function sourceLabel(source: Source, running: boolean): string {
  if (running) return '🔴 Läuft jetzt';
  if (source === 'fav') return '⏳ Dein nächster Act';
  if (source === 'crew') return '⏳ Nächster Crew-Act';
  return '⏳ Nächster Termin';
}

function pickNext(
  acts: Activity[],
  favs: Set<string>,
  votes: VotesMap,
  now: number,
): Picked | null {
  const candidates = acts
    .map((a) => ({ a, start: actStartMs(a) }))
    .filter((entry) => Number.isFinite(entry.start) && !isPast(entry.a, now))
    .sort((x, y) => x.start - y.start);

  if (candidates.length === 0) return null;

  // Priorisierung: Favoriten > Crew-gevotet > beliebig.
  const fav = candidates.find((c) => favs.has(c.a.id));
  if (fav) return { act: fav.a, source: 'fav' };

  const crew = candidates.find((c) => (votes[c.a.id] ?? []).length > 0);
  if (crew) return { act: crew.a, source: 'crew' };

  return { act: candidates[0].a, source: 'any' };
}

function progressPercent(start: number, end: number, now: number): number {
  if (end <= start) return 100;
  const pct = ((now - start) / (end - start)) * 100;
  return Math.max(0, Math.min(100, pct));
}

function formatCountdown(deltaMs: number): string {
  // Negativwerte: 0 anzeigen (z.B. wenn der Tick gerade überkippt).
  const ms = Math.max(0, deltaMs);
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;

  if (days > 0) return `in ${days} Tag${days === 1 ? '' : 'en'} ${hours}h`;
  if (hours > 0) return `in ${hours}h ${pad(minutes)}m`;
  if (minutes > 0) return `in ${minutes}m ${pad(seconds)}s`;
  return `in ${seconds}s`;
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}
